'use client'

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

/**
 * Inline Turnstile widget for catalog-blog (execute mode). Mirrors the
 * canonical impl in
 * catalog-frontend/src/components/form-protection/turnstile-widget.tsx —
 * inlined here to keep catalog-blog a self-contained Next.js app without
 * an internal cross-package import. Keep behaviour in sync if either copy
 * gets updated.
 *
 * Behaviour: pre-renders an invisible widget with `appearance: 'execute'`
 * and stays hidden until the parent calls the imperative `execute()`
 * handle at submit time. Any required interactive challenge then renders
 * as a centered managed overlay (position-fixed), never inline, so it
 * cannot overflow the zero-size container into the surrounding form.
 *
 * Fail-graceful: if `NEXT_PUBLIC_TURNSTILE_SITE_KEY` is unset (local dev /
 * preview) or the script never loads, `execute()` resolves to `null` — the
 * catalog-blog newsletter API route is also fail-open if
 * `TURNSTILE_SECRET_KEY` is unset.
 */

declare global {
	interface Window {
		turnstile?: {
			render: (
				selector: string | HTMLElement,
				opts: {
					sitekey: string
					size?: 'normal' | 'compact' | 'flexible'
					appearance?: 'always' | 'execute' | 'interaction-only'
					callback?: (token: string) => void
					'expired-callback'?: () => void
					'error-callback'?: () => void
					action?: string
				},
			) => string
			execute: (selector: string | HTMLElement, opts?: { action?: string }) => void
			reset: (widgetId?: string) => void
			remove: (widgetId?: string) => void
		}
	}
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

let scriptLoadPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
	if (typeof window === 'undefined') return Promise.resolve()
	if (window.turnstile) return Promise.resolve()
	if (scriptLoadPromise) return scriptLoadPromise
	scriptLoadPromise = new Promise<void>((resolve, reject) => {
		const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`)
		if (existing) {
			existing.addEventListener('load', () => resolve(), { once: true })
			existing.addEventListener('error', () => reject(new Error('turnstile-script-load-failed')), {
				once: true,
			})
			return
		}
		const s = document.createElement('script')
		s.src = SCRIPT_URL
		s.async = true
		s.defer = true
		s.onload = () => resolve()
		s.onerror = () => reject(new Error('turnstile-script-load-failed'))
		document.head.appendChild(s)
	})
	return scriptLoadPromise
}

export interface TurnstileHandle {
	/**
	 * Run the challenge and resolve with a token, or `null` when Turnstile
	 * is unavailable (no sitekey / script not ready) or the challenge
	 * errors/expires. Safe to call repeatedly — resets first so every
	 * submit gets a fresh single-use token.
	 */
	execute: () => Promise<string | null>
}

export interface TurnstileWidgetProps {
	action?: string
}

export const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(function TurnstileWidget(
	{ action },
	ref,
) {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const widgetIdRef = useRef<string | null>(null)
	const pendingRef = useRef<((token: string | null) => void) | null>(null)
	// Resolves once the initial script load + render settles (success OR
	// failure), so execute() can wait for the widget to be ready instead of
	// failing open on a submit fired right after page load.
	const readyRef = useRef<Promise<void> | null>(null)
	const readyResolveRef = useRef<(() => void) | null>(null)
	if (readyRef.current === null) {
		readyRef.current = new Promise<void>((res) => {
			readyResolveRef.current = res
		})
	}
	const [missingKey, setMissingKey] = useState(false)
	const sitekey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

	useEffect(() => {
		if (!sitekey) {
			setMissingKey(true)
			readyResolveRef.current?.()
			return
		}
		let cancelled = false
		loadScript()
			.then(() => {
				if (cancelled || !containerRef.current || !window.turnstile) return
				const id = window.turnstile.render(containerRef.current, {
					sitekey,
					size: 'flexible',
					// `execute` keeps the widget invisible until execute() is
					// called at submit time; the interactive challenge (if any)
					// renders as a fixed overlay, never inline.
					appearance: 'execute',
					action: action ?? 'submit',
					callback: (token) => {
						pendingRef.current?.(token)
						pendingRef.current = null
					},
					'error-callback': () => {
						pendingRef.current?.(null)
						pendingRef.current = null
					},
					'expired-callback': () => {
						pendingRef.current?.(null)
						pendingRef.current = null
					},
				})
				widgetIdRef.current = id
			})
			.catch(() => {})
			.finally(() => readyResolveRef.current?.())
		return () => {
			cancelled = true
			if (widgetIdRef.current && window.turnstile) {
				try {
					window.turnstile.remove(widgetIdRef.current)
				} catch {}
				widgetIdRef.current = null
			}
			// Settle any in-flight execute() before clearing, so a submit
			// awaiting the token doesn't hang forever if we unmount mid-challenge.
			pendingRef.current?.(null)
			pendingRef.current = null
		}
	}, [sitekey, action])

	useImperativeHandle(
		ref,
		() => ({
			execute: async () => {
				if (missingKey || !sitekey) return null
				// Wait (bounded) for the initial load+render to settle so a
				// submit fired right after page load still gets a real token.
				await Promise.race([
					readyRef.current ?? Promise.resolve(),
					new Promise<void>((res) => setTimeout(res, 4000)),
				])
				const ts = window.turnstile
				const widgetId = widgetIdRef.current
				if (!widgetId || !ts?.execute) return null
				return new Promise<string | null>((resolve) => {
					pendingRef.current?.(null)
					pendingRef.current = resolve
					try {
						ts.reset(widgetId)
						ts.execute(containerRef.current ?? widgetId, {
							action: action ?? 'submit',
						})
					} catch {
						pendingRef.current = null
						resolve(null)
					}
				})
			},
		}),
		[missingKey, sitekey, action],
	)

	if (missingKey) return null
	// Clipped container (not display:none — that can stop the iframe from
	// initialising). The interactive challenge renders as a body-level
	// overlay, so the zero size never overflows into the form.
	return (
		<div
			ref={containerRef}
			aria-hidden
			style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
		/>
	)
})
